import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  activateCompany,
  createCompany,
  deactivateCompany,
  listCompanies,
  listCompaniesWithMeta,
  updateCompany,
  type CompanyListResult,
  type CompanyListParams,
} from './api';
import type { Company, CompanyCreateInput, CompanyUpdateInput } from './types';

const KEY = 'companies' as const;
const COMPANY_OPTIONS_KEY = 'company-options' as const;

export const useCompaniesList = (params: CompanyListParams = {}) => {
  return useQuery<Company[], ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listCompanies(params),
  });
};

export const useCompaniesListWithMeta = (params: CompanyListParams = {}) => {
  return useQuery<CompanyListResult, ApiClientError>({
    queryKey: [KEY, 'list-with-meta', params],
    queryFn: () => listCompaniesWithMeta(params),
    placeholderData: (prev) => prev,
  });
};

export const useCreateCompany = () => {
  const qc = useQueryClient();
  return useMutation<Company, ApiClientError, CompanyCreateInput>({
    mutationFn: createCompany,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [COMPANY_OPTIONS_KEY] });
    },
  });
};

export const useUpdateCompany = () => {
  const qc = useQueryClient();
  return useMutation<Company, ApiClientError, { id: string; body: CompanyUpdateInput }>({
    mutationFn: ({ id, body }) => updateCompany(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [COMPANY_OPTIONS_KEY] });
    },
  });
};

export const useDeactivateCompany = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deactivateCompany,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [COMPANY_OPTIONS_KEY] });
    },
  });
};

export const useActivateCompany = () => {
  const qc = useQueryClient();
  return useMutation<Company, ApiClientError, string>({
    mutationFn: activateCompany,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [COMPANY_OPTIONS_KEY] });
    },
  });
};
